import { useLatest } from 'ahooks';
import { groupBy } from 'lodash';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { SliderInput } from '@meitu/saas-ui';
import { Editor } from '@meitu/saas-editor-core';
import type { ChartLayer } from '@meitu/saas-editor-core/es/layers/chart-layer';
import { type ActiveChartInfo } from '@meitu/saas-editor-core/es/layers/chart-layer/actions';
import { nextColorGetter } from '@meitu/saas-editor-core/es/layers/chart-layer/utils/chart-color';
import { getActiveChartLayer } from '@meitu/saas-editor-core/es/layers/chart-layer/utils/core';
import { getPPTChartInfo } from '@meitu/saas-editor-core/es/layers/chart-layer/utils/ppt';
import { type ChartType } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { Button, ColorGroup, TextFontFamily, type FontItem, type ITextFontFamilyProps } from '@meitu/xdesign-widgets';
import { FontSizeSelector } from '@/components/font-size-selector';
import LabelAction from '@/components/label-action';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useLocaleStore } from '@/i18n';
import { getUsingFonts } from '@/utils/font';
import { openChartDataEditor } from './components/chart-data-editor';
import ChartTypeSelector from './components/chart-type-selector';
import CheckBoxWithFilter, { allFilterKey, CheckBoxWithFilterProps } from './components/check-box-with-filter';
import PresetColorsSelector from './components/preset-colors-selector';
import { ActionInfo } from './types';
import { useLayerAndActiveChartInfo } from './utils';
import './chart-layer-panel.less';

interface ActiveChartPanelProps {
    editor: Editor;
    layerId: string;
    isVipUser?: boolean;
    handleShowVip?: () => void;
    children: (param: { ChartPanel: React.ReactNode; TextPanel: React.ReactNode }) => React.ReactElement;
    onAction?: (actionInfo: ActionInfo) => void;
    textFontFamilyComponentUserBizSearchProps?: Pick<ITextFontFamilyProps, 'searchFontData' | 'searchDebounceDelay'>;
}

export interface ActiveChartPanelRef {
    /** 业务使用品牌库文字样式 */
    changeByTextStyle: (textStyle: {
        fontId: number;
        fontFamily: string;
        fontName: string;
        fontSize: number;
        bold: boolean;
        italic: boolean;
        name: string;
    }) => void;
    currentChartType: ChartType;
}

const useUsingFontList = (editor: Editor) => {
    const getUsingFontList = () => {
        return getUsingFonts(editor, editor.services.frame.getLayers(editor.services.frame.getActiveFrameId()));
    };

    const [usingFontList, setUsingFontList] = useState<FontItem[]>(getUsingFontList);
    const refreshUsingFontList = () => setUsingFontList(getUsingFontList());
    return { usingFontList, refreshUsingFontList };
};

/**
 * 使用ChartLayerPanel时, 就默认已经处于编辑态了, 否则会有问题
 */
const ActiveChartLayerPanel = React.forwardRef<ActiveChartPanelRef, ActiveChartPanelProps>(
    (
        {
            editor,
            layerId,
            isVipUser,
            handleShowVip,
            children,
            onAction,
            textFontFamilyComponentUserBizSearchProps: fontFamilyComponentUserBizSearchProps,
        },
        ref,
    ) => {
        const { $t } = useLocaleStore();
        const { layer, activeChartInfo } = useLayerAndActiveChartInfo(editor, layerId);
        const layerRef = useLatest(layer);
        const { usingFontList, refreshUsingFontList } = useUsingFontList(editor);
        const { activeChartLayerActions } = editor.getInstance<ChartLayer>('ChartLayer');
        const globalWidgetRef = useGlobalWidget(editor, false);

        const setFontFamily = (data: FontItem) => {
            onAction?.({ target: 'textFontFamily', data });
            activeChartLayerActions.setFontFamily('all', data.font_name);
            refreshUsingFontList();
        };

        useImperativeHandle(
            ref,
            () => ({
                changeByTextStyle: (textStyle) => {
                    const tableLayer = layerRef.current;
                    if (!tableLayer) return;
                    activeChartLayerActions.setTextStyleBiz('all', textStyle);
                },
                currentChartType: activeChartInfo.type,
            }),
            [activeChartInfo.type],
        );

        useEffect(() => {
            const lastColor = layerRef.current?.data.colors[layerRef.current?.data.colors.length - 1];
            nextColorGetter.resetSource(lastColor || '');
        }, []);

        useEffect(() => {
            const rmListener = editor.getInstance<ChartLayer>('ChartLayer').onEnterEdit(({ layerId }) => {
                const layer = editor.services.flatLayer.getLayer(layerId);
                if (!layer) return;
                openChartDataEditor({
                    editor,
                    onSave: activeChartLayerActions.updateLayerData,
                    onAction,
                });
            });
            return rmListener;
        }, []);

        const setFontSize = (value: number) => {
            onAction?.({ target: 'fontSize', data: value });
            activeChartLayerActions.setFontSize('all', value);
        };
        const setTextColor = (color: { value: `#${string}`; isBrandColor: boolean }) => {
            onAction?.({ target: 'textColor', data: color });
            activeChartLayerActions.setTextColor('all', color.value);
        };

        const getTextPanel = () => {
            return (
                <div className="chart-text-panel">
                    <LabelAction title={$t((v) => v.textFontFamily.fontFamily)} align>
                        <TextFontFamily
                            value={activeChartInfo.textStyle.fontFamily}
                            usingFontList={usingFontList}
                            isVip={isVipUser}
                            allFontData={groupBy(editor.services.font.getFontItems(), 'category_name')}
                            onChange={setFontFamily}
                            onClickVipBtn={handleShowVip}
                            text={activeChartInfo.textList.join('')}
                            onDropdownVisibleChange={(visible) => {
                                globalWidgetRef.current = visible;
                                if (visible) {
                                    onAction?.({ target: 'textFontFamilyDropdownVisible' });
                                }
                            }}
                            {...fontFamilyComponentUserBizSearchProps}
                        />
                    </LabelAction>

                    <LabelAction title={$t((v) => v.textFontSizeAndTextColor.size)} align>
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '188px',
                            }}
                        >
                            <div onClick={() => onAction?.({ target: 'fontSizeClick' })}>
                                <FontSizeSelector
                                    value={activeChartInfo.textStyle.fontSize}
                                    onChange={(value) => value !== null && setFontSize(value)}
                                    onOpenChange={(open) => {
                                        globalWidgetRef.current = open;
                                    }}
                                />
                            </div>
                            <ColorGroup
                                onOpenChange={(open) => {
                                    globalWidgetRef.current = open;
                                }}
                                popupTriggerSize={{
                                    width: 92,
                                    height: 34,
                                }}
                                color={activeChartInfo.textStyle.color}
                                setColor={(color, _, { isBrandColor }) =>
                                    setTextColor({ value: color as `#${string}`, isBrandColor })
                                }
                                isGradient={false}
                                placement="left"
                                onToggleBizTab={(bizTab) => {
                                    if (bizTab === 'team') {
                                        window._stat_track?.('hb_layers_func_click', {
                                            func_id: '1_107',
                                        });
                                    }
                                }}
                            />
                        </div>
                    </LabelAction>

                    {/* 不需要该功能了 */}
                    {/* <FontStyleGroup
                        bold={activeChartInfo.textStyle.bold}
                        writingMode={TextWritingType.HORIZONTAL_TB}
                        italic={activeChartInfo.textStyle.italic}
                        lineThrough={activeChartInfo.textStyle.lineThrough}
                        underline={activeChartInfo.textStyle.underline}
                        closedFeatures={['writingMode', 'listStyle']}
                        onChange={(kind, value) => {
                            onAction?.({
                                target: 'textAttr',
                                type: kind as ChartTextAttrActionType,
                                data: value as boolean,
                            });
                            activeChartLayerActions.setTextAttr(
                                'all',
                                kind as ChartTextAttrActionType,
                                value as boolean,
                            );
                        }}
                    /> */}

                    {/* 不需要该功能了 */}
                    {/* <TextAlignmentGroup
                        textAlign={activeChartInfo.textStyle.textAlign as TextAlignType}
                        writingMode={TextWritingType.HORIZONTAL_TB}
                        onChange={(_, value) => {
                            onAction?.({ target: 'textAlign', type: value });
                            activeChartLayerActions.setTextAlign('all', value as 'left' | 'center' | 'right');
                        }}
                    /> */}
                </div>
            );
        };

        return (
            <div className="chart-layer-panel">
                {children({
                    ChartPanel: (
                        <ChartPanel
                            editor={editor}
                            activeChartInfo={activeChartInfo}
                            activeChartLayerActions={activeChartLayerActions}
                            onAction={onAction}
                        />
                    ),
                    TextPanel: getTextPanel(),
                })}
            </div>
        );
    },
);

ActiveChartLayerPanel.displayName = 'ActiveChartLayerPanel';

export default ActiveChartLayerPanel;

const ChartPanel: React.FC<{
    editor: Editor;
    activeChartInfo: ActiveChartInfo;
    activeChartLayerActions: ChartLayer['activeChartLayerActions'];
    onAction?: (actionInfo: ActionInfo) => void;
}> = ({ editor, activeChartInfo, activeChartLayerActions, onAction }) => {
    const { $t } = useLocaleStore();
    const activeChartInfoRef = useLatest(activeChartInfo);
    const originBorderRadiusRef = useRef<number | null>(null);
    const [labelFilterKey, setLabelFilterKey] = useState<string>(allFilterKey);
    const chartPanelGlobalWidgetRef = useGlobalWidget(editor, false);

    const getBorderRadiusController = () => {
        if (!activeChartInfo.type.includes('bar')) return null;
        return (
            <div className="chart-bar-radius">
                <span>{$t((v) => v.chartLayer.panel.radius)}</span>
                <div>
                    <SliderInput
                        showStepBtn
                        type="horizontal"
                        value={activeChartInfo.itemStyleBorderRadius}
                        min={0}
                        max={activeChartInfo.maxItemStyleBorderRadius}
                        onClick={() => onAction?.({ target: 'borderRaius', data: 'isClick' })}
                        onChange={(value) => {
                            if (originBorderRadiusRef.current === null) {
                                originBorderRadiusRef.current = activeChartInfoRef.current?.itemStyleBorderRadius!;
                            }
                            activeChartLayerActions.setItemStyleBorderRadius(value);
                        }}
                        onAfterChange={(value) => {
                            activeChartLayerActions.setItemStyleBorderRadius(
                                value,
                                originBorderRadiusRef.current ?? undefined,
                            );
                            originBorderRadiusRef.current = null;
                            onAction?.({ target: 'borderRaius', data: 'isUse' });
                        }}
                    />
                </div>
            </div>
        );
    };

    const options: (CheckBoxWithFilterProps & { ignore?: () => boolean; isFirstRow?: boolean })[] = [
        {
            checkBoxName: $t((v) => v.chartLayer.panel.showSection.options.data),
            checkBoxValue: activeChartInfo.showMap.seriesLabel,
            onChange: ({ checked }) => {
                onAction?.({ target: 'showSeriesLabel', data: checked });
                activeChartLayerActions.setShowMap([
                    {
                        position: 'seriesLabel',
                        showState: checked,
                    },
                ]);
            },
            isFirstRow: true,
        },
        {
            checkBoxName: $t((v) => v.chartLayer.panel.showSection.options.gridLine),
            checkBoxValue: activeChartInfo.showMap.grid,
            currentFilterKey: allFilterKey,
            onChange: ({ checked }) => {
                onAction?.({ target: 'showGrid', data: checked });
                activeChartLayerActions.setShowMap([
                    {
                        position: 'grid',
                        showState: checked,
                    },
                ]);
            },
            ignore: () => activeChartInfo.type.includes('pie'),
            isFirstRow: true,
        },
        {
            checkBoxName: $t((v) => v.chartLayer.panel.showSection.options.label),
            checkBoxValue: activeChartInfo.showMap.categoryAxisLabel || activeChartInfo.showMap.valueAxisLabel,
            currentFilterKey: labelFilterKey,
            filters: [
                {
                    key: allFilterKey,
                    label: $t((v) => v.chartLayer.panel.showSection.options.allAxis),
                },
                {
                    key: activeChartInfo.type.includes('horizontal') ? 'valueAxisLabel' : 'categoryAxisLabel',
                    label: $t((v) => v.chartLayer.panel.showSection.options.xAxis),
                },
                {
                    key: activeChartInfo.type.includes('horizontal') ? 'categoryAxisLabel' : 'valueAxisLabel',
                    label: $t((v) => v.chartLayer.panel.showSection.options.yAxis),
                },
            ],
            onChange: ({ changedFilters, changedFilterKey }) => {
                if (changedFilterKey !== null) {
                    setLabelFilterKey(changedFilterKey);
                }
                (['categoryAxisLabel', 'valueAxisLabel'] as const).forEach((position) => {
                    const checked = changedFilters?.find((c) => c.filterKey === position)?.checked ?? null;
                    if (checked !== null) {
                        activeChartLayerActions.setShowMap([
                            {
                                position,
                                showState: checked,
                            },
                        ]);
                    }
                });

                const oldIsShow =
                    !!activeChartInfo.showMap.categoryAxisLabel || !!activeChartInfo.showMap.valueAxisLabel;
                setTimeout(() => {
                    // 500ms后拿到新的信息再去判断
                    const newIsShow =
                        !!activeChartInfoRef.current?.showMap.categoryAxisLabel ||
                        !!activeChartInfoRef.current?.showMap.valueAxisLabel;
                    const hasChangeChecked = (oldIsShow && !newIsShow) || (!oldIsShow && newIsShow);
                    if (hasChangeChecked) {
                        onAction?.({ target: 'changeAxisLabel', data: newIsShow });
                    }
                }, 500);
            },
            ignore: () => activeChartInfo.type.includes('pie'),
        },
        {
            checkBoxName: $t((v) => v.chartLayer.panel.showSection.options.legend),
            checkBoxValue: activeChartInfo.showMap.legend,
            currentFilterKey: activeChartInfo.legendPosition,
            filters: [
                {
                    key: 'top',
                    label: $t((v) => v.chartLayer.panel.showSection.options.top),
                },
                {
                    key: 'bottom',
                    label: $t((v) => v.chartLayer.panel.showSection.options.bottom),
                },
                {
                    key: 'left',
                    label: $t((v) => v.chartLayer.panel.showSection.options.left),
                },
                {
                    key: 'right',
                    label: $t((v) => v.chartLayer.panel.showSection.options.right),
                },
            ],
            onChange: ({ changedFilterKey, checked }) => {
                onAction?.({ target: 'showLegend', data: checked });
                activeChartLayerActions.setShowMap([
                    {
                        position: 'legend',
                        showState: checked,
                        extra:
                            (changedFilterKey as 'top' | 'bottom' | 'left' | 'right' | null) ??
                            activeChartInfo.legendPosition,
                    },
                ]);
            },
        },
    ];
    const displayedOptions = options.filter((opts) => !opts.ignore?.());
    return (
        <div className="chart-panel">
            <Button
                type="text"
                style={{
                    width: '100%',
                    color: 'var(--content-system-link, #38F)',
                    backgroundColor: 'var(--background-btn-tertiary, #F3F5F7)',
                }}
                icon={<IconFont name="EditBold" fontSize={16} color="var(--content-system-link, #38F)" />}
                onClick={() =>
                    openChartDataEditor({
                        editor,
                        onSave: activeChartLayerActions.updateLayerData,
                        onAction,
                    })
                }
            >
                {$t((v) => v.chartLayer.panel.editData)}
            </Button>
            <div className="chart-section">
                <div className="chart-section-name">{$t((v) => v.chartLayer.panel.typeSection.name)}</div>
                <div>
                    <ChartTypeSelector
                        chartType={activeChartInfo.type}
                        onChangeChartType={activeChartLayerActions.changeChartType}
                        onDropdownVisibleChange={(visible) => {
                            chartPanelGlobalWidgetRef.current = visible;
                        }}
                        onTrack={(type) =>
                            onAction?.({ target: 'chartType', data: { actionType: type, isFromEditModal: false } })
                        }
                    />
                </div>
            </div>
            {process.env.NODE_ENV === 'development' && (
                <button
                    onClick={() => {
                        const pptChartInfo = getPPTChartInfo(getActiveChartLayer(editor.id)!);
                        console.log('===pptChartInfo [测试链接: https://jsfiddle.net/8wpuqykc/27/]: ', pptChartInfo);
                    }}
                >
                    打印ppt配置
                </button>
            )}
            <div className="chart-section">
                <div className="chart-section-name">{$t((v) => v.chartLayer.panel.colorSection.name)}</div>
                <div>
                    <PresetColorsSelector
                        currentColors={activeChartInfo.colors}
                        onChangeColors={(newColors) => {
                            const lastColor = newColors[newColors.length - 1];
                            nextColorGetter.resetSource(lastColor);
                            activeChartLayerActions.setColorsPreset(newColors);
                        }}
                        onAction={onAction}
                        onOpenChange={(open) => {
                            chartPanelGlobalWidgetRef.current = open;
                        }}
                    />
                </div>
            </div>
            <div className="chart-section show-section">
                <div className="chart-section-name">{$t((v) => v.chartLayer.panel.showSection.name)}</div>
                <div>
                    <ul className="first-row">
                        {displayedOptions
                            .filter((o) => o.isFirstRow)
                            .map((opt, i) => (
                                <li key={i}>
                                    <CheckBoxWithFilter
                                        key={i}
                                        {...opt}
                                        onDropdownVisibleChange={(visible) => {
                                            chartPanelGlobalWidgetRef.current = visible;
                                        }}
                                    />
                                </li>
                            ))}
                    </ul>
                    {displayedOptions
                        .filter((o) => !o.isFirstRow)
                        .map((opt, i) => (
                            <CheckBoxWithFilter
                                key={i}
                                {...opt}
                                onDropdownVisibleChange={(visible) => {
                                    chartPanelGlobalWidgetRef.current = visible;
                                }}
                            />
                        ))}
                </div>
            </div>

            {getBorderRadiusController()}
        </div>
    );
};

ChartPanel.displayName = 'ChartPanel';
